
import { ReactNode } from 'react';
import { DataFlat, DataStructure, DataKeys } from '../types';
// import components
import { MainFrame, SpacingFrame, Col2ContentFrame } from '../components/display/Frame';
import { ProductCard, StepsCard3, ProductCardNoLink } from '../components/display/Cards';
import { TitleSubtitle, TextBody, TextBulletList, TextParagraphHeader } from '../components/display/TextFormat';
import { ImageImport, ImageImportContain, ImageImportNotSBSSmall } from '../components/display/ImageFormat';
import { VideoPlayer } from '../components/tools/Viewer';
import { SlideInAnim, SlideDirection } from '../components/tools/Animations';
// import functions
import { filterAISolutionsData } from './DataFunctions';
// import context
import { useLang, useScreenSize } from '../components/Context';
// import global var
import { MoreButton } from '../components/tools/Buttons';
// import data
import navUnitsData from '../data/navUnits.json';
const typedNavUnitsData: DataStructure = navUnitsData;

interface DisplayInfoProps {
    contentData: DataStructure;
    imagePath: string;
    title?: string;
    subtitle?: string;
    smallerPadding?: boolean;
    pt?: number;
    pb?: number;
    /** Render rows without outer MainFrame — for nested health-style panels */
    bare?: boolean;
};
export function DisplayInfo2Cols({ contentData, imagePath, title, subtitle, smallerPadding, pt, pb, bare } : DisplayInfoProps) {
    const { lang } = useLang();
    const { screenSize } = useScreenSize();

    function createText(data: DataFlat) {
        let title = data["title"][lang];
        let text = data["text"][lang];
        let subtitle = undefined;
        let list = undefined;
        let button = undefined;
        let text2 = undefined;
        try { subtitle = data["subtitle"][lang] } catch {}
        try { list = data["list"][lang] } catch {}
        try { text2 = data["text2"][lang] } catch {}
        try { button = data["link"] } catch {}

        // Handle button link - check if it should be converted to mailto
        let buttonLink = button ? button["link"] : "";
        if (button && button["link"] === "company-email") {
            buttonLink = `mailto:${typedNavUnitsData["Company_Email"][lang]}`;
        }

        return (
            <SpacingFrame spacing={20}>
                <TitleSubtitle title={title} left />
                { subtitle && !list && <TextBody text={[]} title={subtitle} /> }
                { text.length !== 0 && <TextBody text={text} /> }
                { subtitle && list && <TextBulletList subtitle={subtitle} list={list} subtitleStyle="font-bold" /> }
                { !subtitle && list && <TextBulletList list={list} /> }
                { text2 && <TextBody text={text2} /> }
                { button && <MoreButton label={button["label"]} link={buttonLink} /> }
            </SpacingFrame>
        )
    };

    function createBulletList(data: DataFlat) {
        let title = data["title"][lang];
        let text = data["text"][lang];
        let textContent = data["content"];
        let subtitle = undefined;
        let subtitle2 = undefined;
        let button = undefined;
        try { subtitle = data["subtitle"][lang] } catch {}
        try { subtitle2 = data["subtitle2"][lang] } catch {}
        try { button = data["link"] } catch {}
        let notBullet = data["notBullet"];

        // Handle button link - check if it should be converted to mailto
        let buttonLink = button ? button["link"] : "";
        if (button && button["link"] === "company-email") {
            buttonLink = `mailto:${typedNavUnitsData["Company_Email"][lang]}`;
        }

        return (
            <SpacingFrame spacing={20}>
                <TitleSubtitle title={title} left />
                { subtitle && <TextBody text={[]} title={subtitle} /> }
                { text.length !== 0 && <TextBody text={text} /> }
                { subtitle2 && <TextBody text={[]} title={subtitle2} /> }
                { notBullet
                    ? <TextParagraphHeader data={textContent} />
                    : <TextParagraphHeader data={textContent} isBulletList />
                }
                { button && <MoreButton label={button["label"]} link={buttonLink} /> }
            </SpacingFrame>
        )
    };

    function createImage(data: DataFlat, direction: SlideDirection) {
        let image = data["image"];
        let contained = false;
        let NotSBSSmall = false;
        let maxHeight = 580;
        try { contained = data["contained"] } catch {}
        try { maxHeight = data["maxHeight"] } catch {}
        try { NotSBSSmall = data["notSBSSmall"] } catch {}
        if (contained) {
            return (
                <SlideInAnim direction={direction}>
                    <ImageImportContain image={image} imagePath={imagePath} maxHeight={maxHeight} />
                </SlideInAnim>
            )
        } else if (NotSBSSmall) {
            return (
                <SlideInAnim direction={direction} style={{ margin: "auto" }}>
                    <ImageImportNotSBSSmall images={image} imagePath={imagePath} />
                </SlideInAnim>
            )

        } else {
            return (
                <SlideInAnim direction={direction} style={{ margin: "auto" }}>
                    <ImageImport image={image} imagePath={imagePath} />
                </SlideInAnim>
            )
        };
    };

    function createVideo(data: DataFlat) {
        let link = data["video"];
        let height = 600;
        let label = undefined;
        try { height = data["height"] } catch {}
        try { label = data["label"] } catch {}
        if (label) {
            let text = createText(label);
            return (
                <SpacingFrame spacing={20}>
                    { text }
                    <SlideInAnim direction="bottom">
                        <div className="m-auto w-full h-[100%]">
                            <VideoPlayer url={link} height={ height } />
                        </div>
                    </SlideInAnim>
                </SpacingFrame>
            )
        } else {
            return (
                <SlideInAnim direction="bottom">
                    <div className="m-auto w-full h-[100%]">
                        <VideoPlayer url={link} height={ height } />
                    </div>
                </SlideInAnim>
            )
        };
    };

    // determine type of content and return component
    function determineColType(type: string, data: DataFlat, direction: SlideDirection) {
        let content = <></>
        if (type === "text") {
            content = createText(data);
        } else if (type === "bulletList") {
            content = createBulletList(data);
        } else if (type === "image") {
            content = createImage(data, direction)
        } else if (type === "video") {
            content = createVideo(data);
        };
        return content
    };

    let content : ReactNode = <></>;
    if (contentData) {
        content = Object.keys(contentData).map((i) => {
            let index = Number(i);
            let sectionData = contentData[i];
            let left = undefined;
            let right = undefined;

            Object.keys(sectionData).map((s) => {
                let colData = sectionData[s];
                let colType = colData["type"];

                if (s === "left") {
                    left = determineColType(colType, colData, s);
                } else if (s === "right") {
                    right = determineColType(colType, colData, s);
                };
            });


            if (!left) {
                return (
                    <>{ right }</>
                )
            } else if (!right) {
                return (
                    <>{ left }</>
                )
            } else {
                if (screenSize === "Small") {
                    if (index % 2 == 0) {
                        return (
                            <Col2ContentFrame>
                                { right }
                                { left }
                            </Col2ContentFrame>
                        )
                    } else {
                        return (
                            <Col2ContentFrame>
                                { left }
                                { right }
                            </Col2ContentFrame>
                        )
                    };
                } else {
                    return (
                        <Col2ContentFrame>
                            { left }
                            { right }
                        </Col2ContentFrame>
                    )
                }
            };
        });
    };

    const sectionHeader = title ? (
        <div className="mb-16">
            <TitleSubtitle title={title} subtitle={subtitle} />
        </div>
    ) : null;

    if (bare) {
        return (
            <>
                {sectionHeader}
                <SpacingFrame spacing={128}>
                    {content}
                </SpacingFrame>
            </>
        );
    }

    return (
        <MainFrame smallerPadding={smallerPadding} pt={pt} pb={pb}>
            {sectionHeader}
            <SpacingFrame spacing={128}>
                { content }
            </SpacingFrame>
        </MainFrame>
    )
};


interface DisplayProductCardsProps {
    data: string[];
    haveTag?: boolean;
}
export function DisplayProductCards({ data, haveTag } : DisplayProductCardsProps) {
    const SolutionsData = filterAISolutionsData(data) as DataStructure;
    let solutionsCard = Object.keys(SolutionsData).map((i) => {
        let dataUnit = SolutionsData[i] as DataKeys;
        let imagePath = dataUnit["imagePath"];
        if (dataUnit["noLink"]) {
            return (
                <ProductCardNoLink key={i} data={dataUnit} imagePath={imagePath} haveTag={haveTag} />
            );
        }
        return (
            <ProductCard key={i} data={dataUnit} imagePath={imagePath} haveTag={haveTag} />
        )
    });

    return (
        <>
            { solutionsCard }
        </>
    )
};

export function DisplayProductCardsNoLink({ data, haveTag } : DisplayProductCardsProps) {
    const SolutionsData = filterAISolutionsData(data) as DataStructure;
    let solutionsCard = Object.keys(SolutionsData).map((i) => {
        let dataUnit = SolutionsData[i] as DataKeys;
        let imagePath = dataUnit["imagePath"];
        return (
            <ProductCardNoLink data={dataUnit} imagePath={imagePath} haveTag={haveTag} />
        )
    });

    return (
        <>
            { solutionsCard }
        </>
    )
};


function createImage(data: any, imagePath: string, direction: "left" | "right" = "left") {
    const image = data["image"];
    let contained = false;
    let NotSBSSmall = false;
    let maxHeight = 580;
    try { contained = data["contained"] } catch {}
    try { maxHeight = data["maxHeight"] } catch {}
    try { NotSBSSmall = data["notSBSSmall"] } catch {}

    if (!image) return null;

    if (contained) {
      return (
        <SlideInAnim direction={direction}>
          <ImageImportContain image={image} imagePath={imagePath} maxHeight={maxHeight} />
        </SlideInAnim>
      );
    } else if (NotSBSSmall) {
      return (
        <SlideInAnim direction={direction} style={{ margin: "auto" }}>
          <ImageImportNotSBSSmall images={image} imagePath={imagePath} />
        </SlideInAnim>
      );
    } else {
      return (
        <SlideInAnim direction={direction} style={{ margin: "auto" }}>
          <ImageImport image={image} imagePath={imagePath} />
        </SlideInAnim>
      );
    }
  }


interface ImageTextWithCardsProps {
    contentData: { [key: string]: any };
    imagePath: string;
    smallerPadding?: boolean;
  }

  export function ImageTextWithCards({ contentData, imagePath, smallerPadding }: ImageTextWithCardsProps) {
    const { lang } = useLang();

    const section = contentData?.["0"] || {};
    const steps = contentData?.["steps"] || {};

    const title = section?.right?.title?.[lang] || "";
    const text = section?.right?.text?.[lang] || [];

    const leftData = section?.left || {};
    const imageComponent = createImage(leftData, imagePath, "left");

    return (
        <MainFrame smallerPadding={smallerPadding}>
                <div className="flex flex-col lg:flex-row items-center gap-12">
                {/* Left image */}
                <div className="w-full lg:w-2/5">{imageComponent}</div>

                {/* Right content */}
                <div className="w-full lg:w-3/5 flex flex-col gap-8">
                {/* Upper: title + text */}
                <div>
                <TitleSubtitle title={title} left />
                <div className="h-5" />
                {text.length !== 0 && <TextBody text={text} />}
                </div>

                {/* Lower: cards */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 ">
                    {Object.keys(steps).map((key) => (
                    <StepsCard3 key={key}  data={steps[key]} />
                    ))}
                </div>
                </div>
                </div>
      </MainFrame>
    );
  }